import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { useEditor, EditorContent, Extension } from '@tiptap/react'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import { placeholderDocRanges, rmaChipDocRanges, PLACEHOLDER_HIGHLIGHT_BG, PLACEHOLDER_HIGHLIGHT_TEXT } from './placeholderHighlight.js'
import { RMA_REFERENCE_CHIP } from '@cxhub/shared'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import { Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { AttachmentIcon, ExpandIcon } from './icons.jsx'
import {
  looksLikeHtml, toEditableHtml, fromEditorHtml, hasRichFormatting, plainTextToEditableHtml,
} from './richTextConversion.js'
import { findSignatureBlockStart, onlyBlankAbove } from './templateInsert.js'
import { messageLinkSx, replyEditorSurfaceSx } from './messageColors.js'

// Rich-text replacement for the "Drafted reply (editable)" box
// (SuggestionPanel.jsx) — the only draft box converted to this; every other
// multi-line field in the app (proposed-step notes, the per-step message
// boxes, template/rule bodies elsewhere) stays a plain ExpandableTextField,
// untouched by this file. See richTextConversion.js for the plain<->HTML
// round-trip design (why it's deliberately asymmetric).
//
// No @tiptap/extension-image here (we only need file attachments now, not
// inline image embedding) — confirmed this doesn't regress rendering a PAST
// draft/sent reply that already had an inline image embedded: @tiptap/
// extension-image's own parseHTML defaults to allowBase64: false (never
// overridden here, before or after this change), so a data: URI <img> —
// exactly what the removed "Insert image" button and send-approved-reply.js's
// own recorded humanReply both produced/produce — was ALREADY silently
// dropped on reload before this change; keeping the extension without also
// opting into allowBase64: true would have kept that same broken behavior,
// not fixed it. No other source in this codebase embeds a non-data: <img> in
// draft/reply HTML for the extension to have preserved either.

// The parent inserts templates through a ref (docs/decisions.md, 2026-10-06):
// insertTemplate(html, signatures) puts the template at the agent's cursor when
// the agent placed one since the content was last set programmatically,
// otherwise just above the trailing signature (or at the end when there is
// none), and leaves the cursor after it. captureSelection() records the
// current selection; the panel calls it when the Templates dialog opens.
// isCustomerReply (2026-10-08): the box's text is sent to the customer, so in
// dark mode the whole bordered panel takes the reply-box fill and its links
// the message link color (messageColors.js). Display only: getHTML() and
// what is sent are untouched.
// highlightPlaceholders (2026-10-23, the agent draft panel; on for every
// customer reply since 2026-10-26, defaulting to isCustomerReply): every
// unfilled placeholder (an INSERT token, an XXX, a carrier pick-one list) is
// highlighted in the text. Display only: a ProseMirror decoration lives in
// the view, never in the document, so getHTML(), onChange and what is sent
// never carry it (ui/test/placeholderHighlight.test.js, the e2e Send check).
// The ranges are placeholderHighlight.js's, shared with the plain-text boxes.
const placeholderHighlightKey = new PluginKey('placeholderHighlight')
// The RMA number's token (2026-10-27, @cxhub/shared's rmartn-cust-pays.js)
// shows as a chip, "RMA # — filled in when the return is processed": the
// token's own text is hidden by the class and the chip text drawn from
// data-chip. Display only, like the highlight.
function placeholderDecorations(doc) {
  const decorations = [
    ...placeholderDocRanges(doc).map(({ from, to }) => Decoration.inline(from, to, { class: 'reply-placeholder', 'data-testid': 'reply-placeholder' })),
    ...rmaChipDocRanges(doc).map(({ from, to }) => Decoration.inline(from, to, { class: 'reply-rma-chip', 'data-chip': RMA_REFERENCE_CHIP, 'data-testid': 'reply-rma-chip', title: RMA_REFERENCE_CHIP })),
  ]
  return DecorationSet.create(doc, decorations)
}
const PlaceholderHighlight = Extension.create({
  name: 'placeholderHighlight',
  addProseMirrorPlugins() {
    return [new Plugin({
      key: placeholderHighlightKey,
      state: {
        init: (_config, state) => placeholderDecorations(state.doc),
        apply: (tr, old) => (tr.docChanged ? placeholderDecorations(tr.doc) : old),
      },
      props: { decorations(state) { return placeholderHighlightKey.getState(state) } },
    })]
  },
})

const RichReplyEditor = forwardRef(function RichReplyEditor({ label, value, onChange, disabled, minRows = 4, expandedMinRows = 20, onAddAttachment, onOpenTemplates, isCustomerReply = false, highlightPlaceholders = isCustomerReply }, ref) {
  const [expanded, setExpanded] = useState(false)
  // Whether the agent placed the cursor (clicked or typed in the box) since
  // the content was last set programmatically, and the last selection seen.
  const cursorPlacedRef = useRef(false)
  const savedRangeRef = useRef(null)
  const initialContent = useMemo(() => toEditableHtml(value), []) // eslint-disable-line react-hooks/exhaustive-deps

  const editor = useEditor({
    extensions: [StarterKit, Link.configure({ openOnClick: false, autolink: true }), ...(highlightPlaceholders ? [PlaceholderHighlight] : [])],
    content: initialContent,
    editable: !disabled,
    onUpdate: ({ editor: e }) => onChange(fromEditorHtml(e.getHTML())),
    // aria-label (not a real <label for>, which ProseMirror's contenteditable
    // div has no id to target) so page.getByLabel(label) — every existing
    // e2e test's way of finding this field — still resolves to it.
    editorProps: { attributes: { 'aria-label': label ?? '', role: 'textbox' } },
  }, [label, highlightPlaceholders])

  // Keep the editor in sync with an externally-set value (e.g. a fresh
  // suggestion, a regenerate, an approve/edit reset) without fighting the
  // user's own typing — only reset when the incoming value doesn't match
  // what this editor would itself produce right now.
  //
  // setContent's second argument takes an OPTIONS OBJECT in this installed
  // TipTap version (v3), not the plain boolean second param v2 used — passing
  // `false` here silently destructured to `{ emitUpdate: true }` (a boolean
  // has no `.emitUpdate` property, so the `= true` default always won),
  // meaning this "no-op" resync actually fired onUpdate on every mismatch.
  // Any round-trip difference between `value` and what this editor's own
  // HTML round-trip produces (e.g. a plain `&` surviving escape/unescape
  // asymmetrically) then called onChange with that resynced text WITHOUT the
  // parent's applied-baseline ref ever being updated — SuggestionPanel's
  // isDraftDirty() saw a real (if spurious) divergence and warned on a
  // completely untouched draft. eBay's plain-textarea box has no such
  // resync at all, which is why this only ever showed up for email.
  useEffect(() => {
    if (!editor) return
    const current = fromEditorHtml(editor.getHTML())
    if (current !== (value ?? '')) {
      editor.commands.setContent(toEditableHtml(value), { emitUpdate: false })
      cursorPlacedRef.current = false
      savedRangeRef.current = null
    }
  }, [editor, value])

  useEffect(() => {
    if (!editor) return undefined
    cursorPlacedRef.current = false
    savedRangeRef.current = null
    const save = () => { savedRangeRef.current = { from: editor.state.selection.from, to: editor.state.selection.to } }
    const onFocus = () => { cursorPlacedRef.current = true; save() }
    const onSelection = () => { if (editor.isFocused) save() }
    editor.on('focus', onFocus)
    editor.on('selectionUpdate', onSelection)
    editor.on('blur', save)
    return () => {
      editor.off('focus', onFocus)
      editor.off('selectionUpdate', onSelection)
      editor.off('blur', save)
    }
  }, [editor])

  useImperativeHandle(ref, () => ({
    focus() {
      editor?.commands.focus()
    },
    captureSelection() {
      if (editor && cursorPlacedRef.current) savedRangeRef.current = { from: editor.state.selection.from, to: editor.state.selection.to }
    },
    insertTemplate(html, signatures) {
      if (!editor) return false
      const { doc } = editor.state
      let range
      if (cursorPlacedRef.current && savedRangeRef.current) {
        const max = doc.content.size
        range = { from: Math.min(savedRangeRef.current.from, max), to: Math.min(savedRangeRef.current.to, max) }
        // The cursor in an empty paragraph (the seeded blank line above the
        // signature, 2026-10-12): the template replaces that paragraph rather
        // than splitting it into empty ones around the template.
        const $from = doc.resolve(range.from)
        if (range.from === range.to && $from.depth >= 1 && $from.node(1).isTextblock && !$from.node(1).textContent.trim()) {
          range = { from: $from.before(1), to: $from.after(1) }
        }
      } else if (!doc.textContent.trim() && doc.childCount <= 1) {
        range = { from: 0, to: doc.content.size }
      } else {
        const texts = []
        const offsets = []
        doc.forEach((node, offset) => { texts.push(node.textBetween(0, node.content.size, ' ', ' ')); offsets.push(offset) })
        const at = findSignatureBlockStart(texts, signatures)
        const pos = at >= 0 ? offsets[at] : doc.content.size
        // Only the seeded blank line above the signature: the template takes
        // its place (2026-10-12).
        range = at > 0 && onlyBlankAbove(texts, at) ? { from: 0, to: pos } : { from: pos, to: pos }
      }
      editor.chain().insertContentAt(range, html, { updateSelection: true }).run()
      cursorPlacedRef.current = true
      savedRangeRef.current = { from: editor.state.selection.from, to: editor.state.selection.to }
      return true
    },
  }), [editor])

  // setEditable's own emitUpdate param (a plain boolean, unlike setContent's
  // options object) also defaults to true — and this effect runs on every
  // mount (editor transitioning from null to a real instance is itself a dep
  // change), regardless of whether `disabled` actually changed. Left at the
  // default, it unconditionally emits "update" via a direct call, bypassing
  // the transaction-level preventUpdate gating entirely — firing onChange
  // with whatever editor.getHTML() currently is, with no real edit involved.
  useEffect(() => {
    editor?.setEditable(!disabled, false)
  }, [editor, disabled])

  if (!editor) return null

  return (
    <Stack sx={{ flex: 1, alignSelf: 'stretch', minWidth: 0 }}>
      <Box data-reply-box={isCustomerReply ? 'true' : undefined} sx={[{ border: 1, borderColor: 'divider', borderRadius: 1, p: 0.75 }, (theme) => (isCustomerReply ? replyEditorSurfaceSx(theme) : {})]}>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.25 }}>
          {label && (
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Bold">
            <span>
              <IconButton
                size="small"
                disabled={disabled}
                color={editor.isActive('bold') ? 'primary' : 'default'}
                onClick={() => editor.chain().focus().toggleBold().run()}
              >
                <Typography variant="body2" fontWeight={700} sx={{ width: 16, textAlign: 'center' }}>B</Typography>
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Link">
            <span>
              <IconButton
                size="small"
                disabled={disabled}
                color={editor.isActive('link') ? 'primary' : 'default'}
                onClick={() => {
                  const previousUrl = editor.getAttributes('link').href ?? ''
                  const url = window.prompt('Link URL', previousUrl)
                  if (url === null) return
                  if (!url) { editor.chain().focus().extendMarkRange('link').unsetLink().run(); return }
                  editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
                }}
              >
                <Typography variant="body2" sx={{ width: 16, textAlign: 'center', textDecoration: 'underline' }}>L</Typography>
              </IconButton>
            </span>
          </Tooltip>
          {onAddAttachment && (
            <Tooltip title="Add attachment">
              <span>
                <IconButton size="small" disabled={disabled} aria-label="Add attachment" onClick={onAddAttachment}>
                  <AttachmentIcon fontSize="small" sx={{ color: 'text.primary' }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {onOpenTemplates && (
            <Button size="small" variant="outlined" disabled={disabled} onClick={onOpenTemplates}>
              Templates
            </Button>
          )}
          <IconButton size="small" onClick={() => setExpanded((v) => !v)} title={expanded ? 'Collapse' : 'Expand'}>
            <ExpandIcon expanded={expanded} />
          </IconButton>
        </Stack>
        <Box
          sx={(theme) => ({
            minHeight: `${(expanded ? expandedMinRows : minRows) * 1.5}em`,
            maxHeight: expanded ? '60vh' : `${minRows * 6}em`,
            overflowY: 'auto',
            '& .ProseMirror': { outline: 'none' },
            '& .reply-placeholder': { bgcolor: PLACEHOLDER_HIGHLIGHT_BG, color: PLACEHOLDER_HIGHLIGHT_TEXT, borderRadius: '3px', px: '2px', fontWeight: 600 },
            '& .reply-rma-chip': { fontSize: 0, display: 'inline-block', verticalAlign: 'baseline' },
            '& .reply-rma-chip::before': { content: 'attr(data-chip)', fontSize: '0.8rem', fontWeight: 600, bgcolor: PLACEHOLDER_HIGHLIGHT_BG, color: PLACEHOLDER_HIGHLIGHT_TEXT, border: `1px solid ${PLACEHOLDER_HIGHLIGHT_TEXT}`, borderRadius: '10px', px: '6px', py: '1px' },
            // Bottom margin only (not top) so consecutive paragraphs get
            // exactly one visible gap between them, not a doubled one — a
            // plain plainTextToEditableHtml()-produced <p> per blank-line-
            // separated paragraph relies on this margin for the same visual
            // gap a plain textarea's blank line used to render (margin: 0
            // collapsed every paragraph flush against the next one — see
            // commit a83f630's regression). minHeight keeps a lone empty
            // paragraph (an empty draft, or the gap between two "1A Website"
            // template sections) at a sensible height regardless.
            '& p': { margin: '0 0 1em', minHeight: '1.2em' },
            '& p:last-child': { marginBottom: 0 },
            // An inserted template's HTML often carries its own inline font-
            // family/size (e.g. a Word-doc import's Calibri 11pt) — override
            // so the draft matches the customer's own message font (theme.js's
            // default sans-serif) while it's being edited, not just once sent.
            // 'inherit' resolves through this Box's own ambient MUI context,
            // so it stays in sync automatically if the theme font ever
            // changes. font-weight is untouched — real <strong>/<b>/bold
            // inline styling still renders bold exactly as authored.
            '& *': { fontFamily: 'inherit !important', fontSize: '13px !important' },
            opacity: disabled ? 0.6 : 1,
            ...(isCustomerReply ? messageLinkSx(theme) : {}),
          })}
        >
          <EditorContent editor={editor} />
        </Box>
      </Box>
    </Stack>
  )
})

export default RichReplyEditor

export { looksLikeHtml, toEditableHtml, fromEditorHtml, hasRichFormatting, plainTextToEditableHtml }
